/**
 * ActionsPanel - the Tasks ("next step + owner + due date") on one lead site
 * or site-less account. Paul: "If the next step is not visible when I log in,
 * it does not happen." (Phase 1.2: "Tasks" everywhere, not "Action items".)
 *
 * Owner is a USER PICKER defaulting to the signed-in user (not free text -
 * a typed "Paul" never matched "Paul Sarantos" and the Dashboard card stayed
 * empty). A task can name one of the site's contacts, or its company's
 * (T-02); the contact's name opens their record (onOpenContact when the screen
 * shows it in place, else the #/crm?contact= link). Completing goes through
 * useCompleteTask: an optional completion note first (T-09), then, on the last
 * open task of an active/cold lead, the next step (T-04). Completed tasks stay
 * reachable and can be reopened.
 *
 * With includeSites, a parent Account also lists every Site's tasks,
 * labelled with the site (one rollup rule). A company that has sites passes
 * allowAdd={false}: tasks live on its sites, never on the company (Paul Q12).
 *
 * A task can tag other people besides its owner (TagPeople, 10/6/26); each
 * row names them, with a "Tagged" chip when the signed-in user is only tagged.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import { fmtDate } from '../../utils/format';
import { useApiBase, useDataScope } from '../../context/DataScope';
import { contactIndex, contactsForHome, indexById } from './nextStep';
import { ContactLink, ContactSelect } from '../tasks/TaskComposer';
import TagPeople, { TaggedChip, TaggedNames } from '../tasks/TagPeople';
import { taggedNotOwner, taggedOf, tidyTagged } from '../tasks/tagged';
import { notifyTasksChanged, useTasksChanged } from '../tasks/useTasks';
import { useCompleteTask } from '../tasks/useCompleteTask';
import { localDay } from '../tasks/tasksTable';
import { latestReads } from '../../live/useLive';

// the LOCAL day: the UTC one made "due today" overdue after about 8pm Eastern
const TODAY = () => localDay();
const inputSt = {
  padding: '7px 10px',
  borderRadius: 6,
  border: '1px solid #DDD',
  fontSize: 11,
  fontFamily: 'Montserrat',
  outline: 'none',
  background: '#fff',
};
const btnSm = {
  padding: '3px 9px',
  borderRadius: 6,
  border: '1px solid #DDD',
  background: '#fff',
  color: '#666',
  fontSize: 9.5,
  fontWeight: 700,
  cursor: 'pointer',
  fontFamily: 'Montserrat',
};

// Defaults as parameters - React 19 ignores defaultProps on function components.
export default function ActionsPanel({
  entityType,
  entityId,
  entityLabel = null,
  editable = false,
  includeSites = false,
  allowAdd = true,
  onOpenContact = null,
}) {
  const { users, allAccounts, allProjects } = useApp();
  const { user } = useAuth();
  const me = (user && user.name) || '';
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(null); // { description, owner, due_date, contact_id, tagged }
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [listError, setListError] = useState(''); // loading, cancelling or reopening failed
  const [showDone, setShowDone] = useState(false);
  const base = useApiBase(); // the KSA tab swaps in its own prefix (context/DataScope.js)
  const scope = useDataScope(); // ...and has no CRM contacts
  const { complete, prompt } = useCompleteTask();

  // Its load, the re-read after a change here and a live one (someone else's
  // change) can overlap: the newest answer GOT wins. A read that fails keeps
  // the tasks on screen - a 429 or 502 while every open tab re-reads must not
  // empty the list - and only one the person is waiting for says so.
  const [reads] = useState(latestReads);
  const load = useCallback(
    ({ live } = {}) => {
      const q = `entity_type=${encodeURIComponent(entityType)}&entity_id=${encodeURIComponent(entityId)}${includeSites ? '&include_sites=1' : ''}`;
      const ticket = reads.start();
      fetch(`${base}/crm-actions?${q}`)
        .then((r) => (r.ok ? r.json() : null))
        .then((rows) => {
          if (!Array.isArray(rows)) throw new Error('not a list');
          if (reads.show(ticket)) {
            setItems(rows);
            setListError('');
          }
        })
        .catch(() => {
          if (!live && reads.show(ticket)) setListError('Could not load the tasks - reopen this record to try again.');
        });
    },
    [entityType, entityId, includeSites, base, reads]
  );
  // another record: its own tasks only - a read still out for the last one never lands here
  useEffect(() => {
    reads.show(reads.start());
    setItems([]);
    load();
  }, [load, reads]);
  // a task completed on the Dashboard (or anywhere else) shows here too
  useTasksChanged(load);

  const contacts = useMemo(
    () => (scope ? [] : contactsForHome(entityType, entityId, indexById(allAccounts), indexById(allProjects))),
    [scope, entityType, entityId, allAccounts, allProjects]
  );
  const contactsById = useMemo(() => (scope ? new Map() : contactIndex(allAccounts)), [scope, allAccounts]);
  const contactOf = (x) => (x.contact_id ? contactsById.get(String(x.contact_id)) : null);

  // owner options: every registered user by display name, plus whatever is
  // already on the form (so an old free-text owner still shows while editing)
  const ownerOptions = Array.from(
    new Set([...(users || []).map((u) => u.name).filter(Boolean), ...(form && form.owner ? [form.owner] : [])])
  ).sort((a, b) => a.localeCompare(b));

  const save = async () => {
    if (!form || !form.description.trim() || !form.owner || !form.due_date || busy) return;
    setBusy(true);
    try {
      const { contact_id: contactId, tagged, ...rest } = form;
      const r = await fetch(`${base}/crm-actions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...rest,
          tagged: tidyTagged(tagged, rest.owner),
          entity_type: entityType,
          entity_id: entityId,
          entity_label: entityLabel,
          ...(contactId ? { contact_id: contactId } : {}),
        }),
      });
      if (r.ok) {
        setForm(null);
        setError('');
        notifyTasksChanged();
      } else setError((await r.json().catch(() => ({}))).error || 'Could not save the task - try again.');
    } catch {
      setError('Could not save the task - try again.');
    } finally {
      setBusy(false);
    }
  };
  const setStatus = async (id, status) => {
    setListError('');
    const fail = status === 'open' ? 'Could not reopen the task - try again.' : 'Could not cancel the task - try again.';
    try {
      const r = await fetch(`${base}/crm-actions/${encodeURIComponent(id)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      if (!r.ok) setListError((await r.json().catch(() => ({}))).error || fail);
    } catch {
      setListError(fail);
    }
    notifyTasksChanged();
  };

  const open = items.filter((x) => x.status === 'open');
  const done = items.filter((x) => x.status !== 'open');
  const fromSite = (x) => includeSites && x.entity_id !== entityId;
  const canAdd = editable && allowAdd;

  return (
    <div style={{ background: '#fff', border: '1px solid #ECECEC', borderRadius: 12, padding: '14px 18px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <span style={{ fontSize: 11, fontWeight: 800, color: '#1B5E20', letterSpacing: 1.4, textTransform: 'uppercase' }}>
          Tasks{' '}
          {open.length > 0 && (
            <span style={{ marginLeft: 6, background: '#1B5E20', color: '#fff', borderRadius: 10, padding: '1px 8px', fontSize: 9 }}>
              {open.length} open
            </span>
          )}
        </span>
        {canAdd && !form && (
          <button
            onClick={() => setForm({ description: '', owner: me, due_date: TODAY(), contact_id: '', tagged: [] })}
            style={{
              padding: '5px 12px',
              borderRadius: 6,
              border: '1px solid #1B5E20',
              background: '#fff',
              color: '#1B5E20',
              fontSize: 10,
              fontWeight: 700,
              cursor: 'pointer',
              fontFamily: 'Montserrat',
            }}
          >
            + Add task
          </button>
        )}
      </div>

      {listError && (
        <div
          role="alert"
          style={{
            fontSize: 10.5,
            color: '#B71C1C',
            background: '#FFEBEE',
            borderRadius: 6,
            padding: '6px 10px',
            marginBottom: 10,
            fontWeight: 600,
          }}
        >
          {listError}
        </div>
      )}

      {!allowAdd && (
        <div style={{ fontSize: 10.5, color: '#7B1FA2', background: '#F6EFFA', borderRadius: 6, padding: '6px 10px', marginBottom: 10 }}>
          Tasks go on a site - open one of its sites to add one.
        </div>
      )}

      {form && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            autoFocus
            placeholder="What needs to happen next?"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            style={{ ...inputSt, flex: 2, minWidth: 200 }}
          />
          <select
            value={form.owner}
            onChange={(e) => setForm({ ...form, owner: e.target.value, tagged: tidyTagged(form.tagged, e.target.value) })}
            style={{ ...inputSt, width: 170 }}
            title="Owner"
          >
            {!form.owner && <option value="">Owner…</option>}
            {ownerOptions.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
          <TagPeople
            value={form.tagged}
            onChange={(v) => setForm((f) => ({ ...f, tagged: v }))}
            people={ownerOptions}
            owner={form.owner}
            style={{ flex: '1 1 200px' }}
          />
          <input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} style={inputSt} />
          {contacts.length > 0 && (
            <ContactSelect
              contacts={contacts}
              value={form.contact_id}
              onChange={(v) => setForm({ ...form, contact_id: v })}
              style={{ ...inputSt, width: 190 }}
            />
          )}
          <button
            onClick={save}
            disabled={busy || !form.description.trim() || !form.owner || !form.due_date}
            style={{
              padding: '7px 16px',
              borderRadius: 6,
              border: 'none',
              background: '#1B5E20',
              color: '#fff',
              fontSize: 10.5,
              fontWeight: 700,
              cursor: 'pointer',
              fontFamily: 'Montserrat',
              opacity: !form.description.trim() || !form.owner || !form.due_date ? 0.5 : 1,
            }}
          >
            Save
          </button>
          <button
            onClick={() => {
              setForm(null);
              setError('');
            }}
            style={{
              padding: '7px 12px',
              borderRadius: 6,
              border: '1px solid #DDD',
              background: '#fff',
              color: '#666',
              fontSize: 10.5,
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'Montserrat',
            }}
          >
            Cancel
          </button>
          {error && <div style={{ flexBasis: '100%', fontSize: 10.5, color: '#B71C1C', fontWeight: 600 }}>{error}</div>}
        </div>
      )}

      {open.length === 0 && !form && (
        <div style={{ fontSize: 11, color: '#aaa' }}>
          {allowAdd ? 'No open tasks - the next step lives here.' : 'No open tasks on its sites.'}
        </div>
      )}
      {open.map((x) => {
        const overdue = x.due_date && String(x.due_date).slice(0, 10) < TODAY();
        const contact = contactOf(x);
        return (
          <div key={x.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', borderBottom: '1px solid #F4F4F0' }}>
            {editable && (
              <input type="checkbox" title="Mark complete" checked={false} onChange={() => complete(x)} style={{ cursor: 'pointer' }} />
            )}
            <span style={{ flex: 1, fontSize: 11.5, color: '#0D0D0D', fontWeight: 600 }}>{x.description}</span>
            {fromSite(x) && x.entity_label && (
              <span
                style={{
                  fontSize: 9,
                  fontWeight: 700,
                  color: '#7B1FA2',
                  background: '#F6EFFA',
                  borderRadius: 10,
                  padding: '1px 8px',
                  whiteSpace: 'nowrap',
                }}
              >
                {x.entity_label}
              </span>
            )}
            {contact && (
              <span style={{ fontSize: 10, whiteSpace: 'nowrap' }}>
                <ContactLink contact={contact} onOpen={onOpenContact || undefined} />
              </span>
            )}
            {taggedNotOwner(x, user, users) && <TaggedChip />}
            <span style={{ fontSize: 10, color: '#888', whiteSpace: 'nowrap' }}>{x.owner}</span>
            <TaggedNames names={taggedOf(x)} style={{ fontSize: 10 }} />
            <span style={{ fontSize: 10, fontWeight: 800, color: overdue ? '#B71C1C' : '#1565C0', whiteSpace: 'nowrap' }}>
              {overdue ? 'OVERDUE · ' : 'due '}
              {fmtDate(x.due_date)}
            </span>
            {editable && (
              <button
                onClick={() => {
                  if (window.confirm(`Cancel this task?\n\n"${x.description}"`)) setStatus(x.id, 'cancelled');
                }}
                title="Cancel this task"
                style={{ border: 'none', background: 'none', color: '#bbb', cursor: 'pointer', fontSize: 12 }}
              >
                ×
              </button>
            )}
          </div>
        );
      })}

      {done.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <button onClick={() => setShowDone(!showDone)} style={{ ...btnSm, border: 'none', padding: 0, color: '#888' }}>
            {showDone ? '▾' : '▸'} {done.length} completed / cancelled
          </button>
          {showDone &&
            done.map((x) => {
              const contact = contactOf(x);
              return (
                <div
                  key={x.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '5px 0',
                    borderBottom: '1px solid #F7F7F4',
                    opacity: 0.8,
                  }}
                >
                  <span
                    style={{
                      fontSize: 9,
                      fontWeight: 800,
                      color: x.status === 'complete' ? '#1B5E20' : '#999',
                      textTransform: 'uppercase',
                      width: 64,
                    }}
                  >
                    {x.status === 'complete' ? 'done' : 'cancelled'}
                  </span>
                  <span style={{ flex: 1, fontSize: 11, color: '#555', textDecoration: x.status === 'complete' ? 'line-through' : 'none' }}>
                    {x.description}
                  </span>
                  {fromSite(x) && x.entity_label && <span style={{ fontSize: 9, color: '#7B1FA2' }}>{x.entity_label}</span>}
                  {contact && (
                    <span style={{ fontSize: 9.5, whiteSpace: 'nowrap' }}>
                      <ContactLink contact={contact} onOpen={onOpenContact || undefined} />
                    </span>
                  )}
                  <span style={{ fontSize: 9.5, color: '#999', whiteSpace: 'nowrap' }}>{x.owner}</span>
                  <TaggedNames names={taggedOf(x)} style={{ fontSize: 9.5, color: '#999' }} />
                  <span style={{ fontSize: 9.5, color: '#999', whiteSpace: 'nowrap' }}>
                    {x.status === 'complete' && x.completed_at ? `completed ${fmtDate(x.completed_at)}` : `was due ${fmtDate(x.due_date)}`}
                  </span>
                  {editable && (
                    <button onClick={() => setStatus(x.id, 'open')} style={btnSm} title="Put this back on the open list">
                      Reopen
                    </button>
                  )}
                </div>
              );
            })}
        </div>
      )}
      {prompt}
    </div>
  );
}
